import {FC, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {FixedSizeList as VirtualList} from "react-window";
import InfiniteLoader from "react-window-infinite-loader";
import {
    Avatar,
    Badge,
    Button,
    Checkbox,
    Dropdown,
    Input,
    Modal,
    Select,
    Space,
    Spin,
    Tooltip,
    Typography,
} from "antd";
import type {MenuProps} from "antd";
import {
    ArrowLeftOutlined,
    BankOutlined,
    ClockCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    DownOutlined,
    EnvironmentOutlined,
    InboxOutlined,
    MailOutlined,
    PhoneOutlined,
    SearchOutlined,
    SendOutlined,
    SortAscendingOutlined,
    SortDescendingOutlined,
    TagOutlined,
    UndoOutlined,
    UserOutlined,
} from "@ant-design/icons";
import {useMutation, useQuery, useQueryClient, UseQueryResult} from "@tanstack/react-query";
import {useNavigate, useParams} from "react-router-dom";
import {formatDistanceToNow} from "date-fns";
import {de} from "date-fns/locale";
import styled from "styled-components";
import {useMediaQuery} from "@uidotdev/usehooks";

import {useMyMessages} from "./hooks/useMyMessages.tsx";
import {IMessage, IMessageBuble} from "../../services/types.ts";
import {
    getCandidateFromMsgByIdApi,
    getCandidateMessagesByComId,
    sendMessageApi,
    setConversationHidden,
    setConversationLabel,
} from "../../services/api.ts";
import {MessageItem} from "./components/MessageItem.tsx";
import {AbsLoader} from "../candidatePool/CandidatePool.tsx";
import {colors} from "../../util/colors.ts";
import {AuthContext} from "../../context/AuthContext.tsx";
import {
    ConversationLabel,
    getInitials,
    LABEL_CONFIG,
    LABEL_OPTIONS,
    LABEL_TABS,
} from "./constants.ts";

const {Text, Title} = Typography;
const {TextArea} = Input;

const LIMIT = 50;
const SIDEBAR_WIDTH = 400;
const INFO_PANEL_WIDTH = 300;
const ROW_HEIGHT = 124;

type LabelFilter = ConversationLabel | "all";

type FirstMessageType = Omit<IMessage, "_candidate_2_0" | "_client" | "_job_demand_with_labels">;
type CombinedMessageType = IMessageBuble | FirstMessageType;

const getCandidateDisplayName = (item?: IMessage | null): string => {
    if (!item) return "";
    if (item.contact_data === "visible" && item._candidate_2_0?.contact) {
        const {first_name, last_name} = item._candidate_2_0.contact;
        if (first_name || last_name) return `${first_name || ""} ${last_name || ""}`.trim();
    }
    if (item._candidate_2_0?.main_job?.job_position) return "Anonymer Kandidat";
    return "Unbekannter Kandidat";
};

export const Messages: FC = () => {
    const {user} = useContext(AuthContext);
    const navigate = useNavigate();
    const {id: urlKey} = useParams<{id: string}>();
    const isMobile = useMediaQuery("only screen and (max-width : 768px)");
    const queryClient = useQueryClient();

    const showHidden = false;

    // ── Decline modal state ──────────────────────────────────────────────
    const [declineOpen, setDeclineOpen] = useState(false);
    const [declineText, setDeclineText] = useState("");

    // ── Mass selection state ─────────────────────────────────────────────
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

    const {
        data,
        isFetching,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useMyMessages(LIMIT, showHidden);

    const listRef = useRef<VirtualList | null>(null);

    const flattenedData = useMemo<IMessage[]>(() => {
        if (!data || (data.pages.length === 1 && data.pages[0][0]?.id === 0)) return [];
        return data.pages.flatMap((page) => (Array.isArray(page) ? page : []));
    }, [data]);

    // ── Selection (synced with URL key) ──────────────────────────────────
    const [selectedKey, setSelectedKey] = useState<string | null>(urlKey || null);

    useEffect(() => {
        if (urlKey) setSelectedKey(urlKey);
    }, [urlKey]);

    // ── Search + label filter (UI-only) ──────────────────────────────────
    const [search, setSearch] = useState("");
    const [labelFilter, setLabelFilter] = useState<LabelFilter>("all");
    const [jobFilter, setJobFilter] = useState<number | "all">("all");
    const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
    const [reply, setReply] = useState("");

    // Unique jobs across loaded conversations, for the "Alle Stellen" filter.
    const jobOptions = useMemo(() => {
        const map = new Map<number, string>();
        flattenedData.forEach((m) => {
            const j = m._job_demand_with_labels?.[0];
            if (j?.id) map.set(j.id, j.title || `Stelle #${j.id}`);
        });
        return Array.from(map, ([value, label]) => ({value, label}));
    }, [flattenedData]);

    const filtered = useMemo(() => {
        let r = flattenedData;
        if (search.trim()) {
            const q = search.toLowerCase();
            r = r.filter(
                (m) =>
                    getCandidateDisplayName(m).toLowerCase().includes(q) ||
                    (m._job_demand_with_labels?.[0]?.title || "").toLowerCase().includes(q) ||
                    (m.message || "").toLowerCase().includes(q),
            );
        }
        if (labelFilter !== "all") {
            r = r.filter((m) => m.label === labelFilter);
        }
        if (jobFilter !== "all") {
            r = r.filter((m) => m._job_demand_with_labels?.[0]?.id === jobFilter);
        }
        return [...r].sort((a, b) =>
            sortOrder === "newest"
                ? new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                : new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
    }, [flattenedData, search, labelFilter, jobFilter, sortOrder]);

    // Auto-select the top conversation on desktop when none is selected.
    useEffect(() => {
        if (!isMobile && !selectedKey && filtered.length > 0) {
            const first = filtered[0];
            setSelectedKey(first.key);
            navigate(`/messages/${first.key}`, {replace: true});
        }
    }, [isMobile, selectedKey, filtered, navigate]);

    // ── Fetch the selected conversation context + messages ───────────────
    // NOTE: client-side getCandidateFromMsgByIdApi returns an array in practice
    // (see existing MessageDetail.tsx behavior — uses `data?.[0]`).
    const {data: msgDetailsArr, isLoading: detailsLoading}: UseQueryResult<IMessage[], Error> = useQuery({
        queryKey: ["candidate-det", selectedKey],
        queryFn: () => getCandidateFromMsgByIdApi(selectedKey!),
        enabled: !!selectedKey,
    });
    const msgDetails = msgDetailsArr?.[0];

    const {data: thread, isLoading: threadLoading}: UseQueryResult<IMessageBuble[], Error> = useQuery({
        queryKey: ["message", msgDetails?.id],
        queryFn: () => getCandidateMessagesByComId(msgDetails!.id),
        enabled: !!msgDetails?.id,
    });

    const sendMessageMutation = useMutation({
        mutationFn: sendMessageApi,
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["message", msgDetails!.id]});
            setReply("");
        },
    });

    const labelMutation = useMutation({
        mutationFn: ({id, label}: {id: number; label: ConversationLabel | null}) =>
            setConversationLabel(id, label),
        onSuccess: (_data, variables) => {
            // Update the detail cache directly — the GET endpoint may not return
            // the updated `label` (esp. in production), so don't rely on refetch.
            queryClient.setQueryData<IMessage[]>(
                ["candidate-det", selectedKey],
                (old) =>
                    old?.map((m) =>
                        m.id === variables.id ? {...m, label: variables.label} : m,
                    ),
            );
            queryClient.invalidateQueries({queryKey: ["messages"]});
        },
    });

    const declineMutation = useMutation({
        mutationFn: async ({msg, message}: {msg: IMessage; message: string}) => {
            await sendMessageApi({
                message,
                type: "outreach",
                client_id: msg.client_id,
                candidate_id: msg._candidate_2_0?.id || msg.candidate_id,
                job_demand_id: msg._job_demand_with_labels?.[0]?.id || 0,
                communication_id: msg.id,
                contact_data: msg.contact_data,
            });
            await setConversationLabel(msg.id, "rejected");
            return msg.id;
        },
        onSuccess: (id) => {
            queryClient.setQueryData<IMessage[]>(
                ["candidate-det", selectedKey],
                (old) => old?.map((m) => (m.id === id ? {...m, label: "rejected"} : m)),
            );
            queryClient.invalidateQueries({queryKey: ["messages"]});
            queryClient.invalidateQueries({queryKey: ["message", id]});
            setDeclineOpen(false);
        },
    });

    const bulkLabelMutation = useMutation({
        mutationFn: async ({ids, label}: {ids: number[]; label: ConversationLabel | null}) => {
            await Promise.all(ids.map((id) => setConversationLabel(id, label)));
            return ids;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["messages"]});
            queryClient.invalidateQueries({queryKey: ["candidate-det", selectedKey]});
            setSelectedIds(new Set());
        },
    });

    const bulkHideMutation = useMutation({
        mutationFn: async ({ids, hidden}: {ids: number[]; hidden: boolean}) => {
            await Promise.all(ids.map((id) => setConversationHidden(id, hidden)));
            return {ids, hidden};
        },
        onSuccess: ({hidden}) => {
            queryClient.invalidateQueries({queryKey: ["messages"]});
            queryClient.invalidateQueries({queryKey: ["candidate-det", selectedKey]});
            setSelectedIds(new Set());
            // If we just hid the currently selected conversation, clear it.
            if (hidden && !showHidden && msgDetails && selectedIds.has(msgDetails.id)) {
                setSelectedKey(null);
                navigate("/messages", {replace: true});
            }
        },
    });

    const hideMutation = useMutation({
        mutationFn: ({id, hidden}: {id: number; hidden: boolean}) =>
            setConversationHidden(id, hidden),
        onSuccess: (_data, variables) => {
            queryClient.setQueryData<IMessage[]>(
                ["candidate-det", selectedKey],
                (old) =>
                    old?.map((m) =>
                        m.id === variables.id ? {...m, client_hidden: variables.hidden} : m,
                    ),
            );
            queryClient.invalidateQueries({queryKey: ["messages"]});
            // When hiding in the default view, drop selection so the user sees the list refresh.
            if (variables.hidden && !showHidden) {
                setSelectedKey(null);
                navigate("/messages", {replace: true});
            }
        },
    });

    // Combined chat list: original first message + bubbles (sorted by time)
    const messageList: CombinedMessageType[] = useMemo(() => {
        if (!msgDetails) return [];
        const {_candidate_2_0: _c2, _client: _c, _job_demand_with_labels: _j, ...firstMessage} = msgDetails;
        void _c2; void _c; void _j;
        const bubbles = thread ?? [];
        return [...bubbles, firstMessage].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
        );
    }, [msgDetails, thread]);

    // ── Handlers ─────────────────────────────────────────────────────────
    const handleSelect = useCallback(
        (item: IMessage) => {
            setSelectedKey(item.key);
            navigate(`/messages/${item.key}`, {replace: true});
        },
        [navigate],
    );

    const handleBack = useCallback(() => {
        setSelectedKey(null);
        navigate("/messages", {replace: true});
    }, [navigate]);

    const handleSend = useCallback(() => {
        if (!reply.trim() || !msgDetails) return;
        sendMessageMutation.mutate({
            message: reply,
            type: "outreach",
            client_id: msgDetails.client_id,
            candidate_id: msgDetails._candidate_2_0?.id || msgDetails.candidate_id,
            job_demand_id: msgDetails._job_demand_with_labels?.[0]?.id || 0,
            communication_id: msgDetails.id,
            contact_data: msgDetails.contact_data,
        });
    }, [reply, msgDetails, sendMessageMutation]);

    // ── Virtualized list ─────────────────────────────────────────────────
    const itemCount = hasNextPage ? filtered.length + 1 : filtered.length;
    const loadMoreItems = useCallback(() => {
        if (isFetchingNextPage) return Promise.resolve();
        return fetchNextPage().then(() => {});
    }, [isFetchingNextPage, fetchNextPage]);
    const isItemLoaded = useCallback(
        (index: number) => !hasNextPage || index < filtered.length,
        [hasNextPage, filtered.length],
    );

    // ── Scroll thread to bottom on update ────────────────────────────────
    const threadEndRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        threadEndRef.current?.scrollIntoView({behavior: "smooth"});
    }, [messageList.length]);

    // ── Computed display ─────────────────────────────────────────────────
    const candidateName = getCandidateDisplayName(msgDetails);
    const jobTitle = msgDetails?._job_demand_with_labels?.[0]?.title || "";
    const currentLabel = msgDetails?.label ?? null;
    const labelCfg = currentLabel ? LABEL_CONFIG[currentLabel] : null;
    const job = msgDetails?._job_demand_with_labels?.[0];
    const candidate = msgDetails?._candidate_2_0;
    const contactVisible = msgDetails?.contact_data === "visible";
    const isHidden = !!msgDetails?.client_hidden;

    const labelCounts = useMemo(() => {
        const out: Record<string, number> = {all: flattenedData.length};
        flattenedData.forEach((m) => {
            if (m.label) out[m.label] = (out[m.label] || 0) + 1;
        });
        return out;
    }, [flattenedData]);

    const labelMenu: MenuProps = useMemo(() => ({
        items: [
            ...LABEL_OPTIONS.map((opt) => {
                const cfg = LABEL_CONFIG[opt.value];
                return {
                    key: opt.value,
                    label: (
                        <MenuChip $bg={cfg.bg} $color={cfg.color}>
                            {cfg.label}
                        </MenuChip>
                    ),
                };
            }),
            {type: "divider" as const},
            {key: "__clear__", label: <MenuClear>Kein Label</MenuClear>},
        ],
        onClick: ({key}) => {
            if (!msgDetails) return;
            const value = key === "__clear__" ? null : (key as ConversationLabel);
            labelMutation.mutate({id: msgDetails.id, label: value});
        },
    }), [msgDetails, labelMutation]);

    // Bulk-label menu (same chip rendering, applies to selected ids)
    const bulkLabelMenu: MenuProps = useMemo(() => ({
        items: [
            ...LABEL_OPTIONS.map((opt) => {
                const cfg = LABEL_CONFIG[opt.value];
                return {
                    key: opt.value,
                    label: (
                        <MenuChip $bg={cfg.bg} $color={cfg.color}>
                            {cfg.label}
                        </MenuChip>
                    ),
                };
            }),
            {type: "divider" as const},
            {key: "__clear__", label: <MenuClear>Kein Label</MenuClear>},
        ],
        onClick: ({key}) => {
            if (selectedIds.size === 0) return;
            const value = key === "__clear__" ? null : (key as ConversationLabel);
            bulkLabelMutation.mutate({ids: Array.from(selectedIds), label: value});
        },
    }), [bulkLabelMutation, selectedIds]);

    // ── Mass selection helpers ───────────────────────────────────────────
    const handleToggleMassSelect = useCallback((item: IMessage) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(item.id)) next.delete(item.id);
            else next.add(item.id);
            return next;
        });
    }, []);

    const anyMassSelected = selectedIds.size > 0;
    const filteredIds = useMemo(() => filtered.map((m) => m.id), [filtered]);
    const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

    const toggleSelectAll = useCallback(() => {
        setSelectedIds((prev) => {
            if (filteredIds.every((id) => prev.has(id))) {
                const next = new Set(prev);
                filteredIds.forEach((id) => next.delete(id));
                return next;
            }
            const next = new Set(prev);
            filteredIds.forEach((id) => next.add(id));
            return next;
        });
    }, [filteredIds]);

    // ── Decline modal helpers ────────────────────────────────────────────
    const buildDeclineTemplate = useCallback((name: string) => {
        const greeting =
            name && name !== "Unbekannter Kandidat" && name !== "Anonymer Kandidat"
                ? `Sehr geehrte/r ${name},`
                : "Sehr geehrte/r Kandidat/in,";
        return `${greeting}

vielen Dank für Ihr Interesse und Ihre Bewerbung. Nach sorgfältiger Prüfung müssen wir Ihnen leider mitteilen, dass wir uns für einen anderen Kandidaten entschieden haben.

Wir wünschen Ihnen für Ihre weitere Suche alles Gute.`;
    }, []);

    const openDeclineModal = useCallback(() => {
        if (!msgDetails) return;
        setDeclineText(buildDeclineTemplate(candidateName));
        setDeclineOpen(true);
    }, [msgDetails, candidateName, buildDeclineTemplate]);

    const handleSendDecline = useCallback(() => {
        if (!msgDetails || !declineText.trim()) return;
        declineMutation.mutate({msg: msgDetails, message: declineText});
    }, [msgDetails, declineText, declineMutation]);

    const showThread = !!selectedKey;

    // ── Render ───────────────────────────────────────────────────────────
    return (
        <Outer>
            {isFetching && !flattenedData.length && (
                <AbsLoader>
                    <Spin size="large"/>
                </AbsLoader>
            )}

            {/* LEFT: Conversation List */}
            {(!isMobile || !showThread) && (
                <Sidebar>
                    <SidebarHeader>
                        <HeaderRow>
                            <Title level={4} style={{margin: 0}}>
                                {showHidden ? "Ausgeblendete" : "Nachrichten"}
                            </Title>
                            {flattenedData.length > 0 && (
                                <Badge
                                    count={flattenedData.length}
                                    style={{backgroundColor: colors.main}}
                                />
                            )}
                        </HeaderRow>
                        <Input
                            placeholder="Kandidat oder Stelle suchen…"
                            prefix={<SearchOutlined style={{color: "#bfbfbf"}}/>}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            allowClear
                            style={{marginBottom: 10}}
                        />
                        <FilterRow>
                            <Select
                                value={jobFilter}
                                onChange={(v) => setJobFilter(v)}
                                style={{flex: 1}}
                                options={[{value: "all", label: "Alle Stellen"}, ...jobOptions]}
                            />
                            <Tooltip title={sortOrder === "newest" ? "Neueste zuerst" : "Älteste zuerst"}>
                                <Button
                                    icon={sortOrder === "newest" ? <SortDescendingOutlined/> : <SortAscendingOutlined/>}
                                    onClick={() => setSortOrder((p) => (p === "newest" ? "oldest" : "newest"))}
                                />
                            </Tooltip>
                        </FilterRow>
                        <TabsRow>
                            {LABEL_TABS.map((tab) => {
                                const active = labelFilter === tab.key;
                                const count = tab.key === "all" ? labelCounts.all : labelCounts[tab.key] || 0;
                                return (
                                    <TabButton
                                        key={tab.key}
                                        $active={active}
                                        onClick={() => setLabelFilter(tab.key)}
                                    >
                                        {tab.label}
                                        {count > 0 && <TabCount>{count}</TabCount>}
                                    </TabButton>
                                );
                            })}
                        </TabsRow>
                        {anyMassSelected && (
                            <MassActionBar>
                                <SelectAllLabel onClick={toggleSelectAll}>
                                    <Checkbox
                                        checked={allFilteredSelected}
                                        indeterminate={!allFilteredSelected && anyMassSelected}
                                        onChange={toggleSelectAll}
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                    <span>Alle ({filtered.length})</span>
                                </SelectAllLabel>
                                <MassCount>{selectedIds.size} ausgewählt</MassCount>
                                <Dropdown
                                    menu={bulkLabelMenu}
                                    trigger={["click"]}
                                    disabled={bulkLabelMutation.isPending}
                                >
                                    <Button
                                        size="small"
                                        icon={<TagOutlined/>}
                                        loading={bulkLabelMutation.isPending}
                                    >
                                        Label
                                    </Button>
                                </Dropdown>
                                <Tooltip title={showHidden ? "Wiederherstellen" : "Löschen"}>
                                    <Button
                                        size="small"
                                        danger={!showHidden}
                                        icon={showHidden ? <UndoOutlined/> : <DeleteOutlined/>}
                                        loading={bulkHideMutation.isPending}
                                        onClick={() =>
                                            bulkHideMutation.mutate({
                                                ids: Array.from(selectedIds),
                                                hidden: !showHidden,
                                            })
                                        }
                                    >
                                        {selectedIds.size}
                                    </Button>
                                </Tooltip>
                            </MassActionBar>
                        )}
                    </SidebarHeader>
                    <ListArea>
                        {filtered.length === 0 ? (
                            <EmptyList>
                                <Text type="secondary">
                                    {showHidden
                                        ? "Keine ausgeblendeten Gespräche"
                                        : "Keine Gespräche gefunden"}
                                </Text>
                            </EmptyList>
                        ) : (
                            <InfiniteLoader
                                isItemLoaded={isItemLoaded}
                                itemCount={itemCount}
                                loadMoreItems={loadMoreItems}
                            >
                                {({onItemsRendered, ref}) => (
                                    <VirtualList
                                        height={window.innerHeight - 260}
                                        itemCount={itemCount}
                                        itemSize={ROW_HEIGHT}
                                        onItemsRendered={onItemsRendered}
                                        ref={(instance) => {
                                            ref(instance);
                                            listRef.current = instance;
                                        }}
                                        width="100%"
                                    >
                                        {({index, style}) => {
                                            const item = filtered[index];
                                            if (!item) {
                                                return (
                                                    <div style={style}>
                                                        <RowSpinner><Spin size="small"/></RowSpinner>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div style={style}>
                                                    <MessageItem
                                                        item={item}
                                                        selected={item.key === selectedKey}
                                                        onSelect={handleSelect}
                                                        onToggleHidden={(m) =>
                                                            hideMutation.mutate({
                                                                id: m.id,
                                                                hidden: !m.client_hidden,
                                                            })
                                                        }
                                                        toggleHiddenLoading={
                                                            hideMutation.isPending &&
                                                            hideMutation.variables?.id === item.id
                                                        }
                                                        massSelected={selectedIds.has(item.id)}
                                                        anyMassSelected={anyMassSelected}
                                                        onToggleMassSelect={handleToggleMassSelect}
                                                    />
                                                </div>
                                            );
                                        }}
                                    </VirtualList>
                                )}
                            </InfiniteLoader>
                        )}
                    </ListArea>
                </Sidebar>
            )}

            {/* CENTER: Thread */}
            {(!isMobile || showThread) && (
                <Center>
                    {!selectedKey ? (
                        <EmptyState>
                            <InboxOutlined style={{fontSize: 64, marginBottom: 16, color: "#bfbfbf"}}/>
                            <Title level={4} type="secondary">Wählen Sie ein Gespräch aus</Title>
                            <Text type="secondary">
                                Klicken Sie links auf ein Gespräch, um den Verlauf zu sehen.
                            </Text>
                        </EmptyState>
                    ) : detailsLoading || !msgDetails ? (
                        <CenterLoader><Spin size="large"/></CenterLoader>
                    ) : (
                        <>
                            <ChatHeader>
                                <ChatHeaderLeft>
                                    {isMobile && (
                                        <Button
                                            type="text"
                                            icon={<ArrowLeftOutlined/>}
                                            onClick={handleBack}
                                            style={{marginRight: 4}}
                                        />
                                    )}
                                    <Avatar
                                        size={36}
                                        style={{backgroundColor: colors.main, fontSize: 14, fontWeight: 600, flexShrink: 0}}
                                    >
                                        {getInitials(candidateName)}
                                    </Avatar>
                                    <ChatHeaderTitles>
                                        <Text strong style={{fontSize: 15}} ellipsis>{candidateName}</Text>
                                        <Text type="secondary" style={{fontSize: 12}} ellipsis>{jobTitle}</Text>
                                    </ChatHeaderTitles>
                                </ChatHeaderLeft>
                                <Space>
                                    <Dropdown
                                        menu={labelMenu}
                                        trigger={["click"]}
                                        disabled={labelMutation.isPending}
                                    >
                                        {labelCfg ? (
                                            <LabelButton
                                                $bg={labelCfg.bg}
                                                $color={labelCfg.color}
                                                disabled={labelMutation.isPending}
                                            >
                                                <TagOutlined style={{fontSize: 11}}/>
                                                {labelCfg.label}
                                                <DownOutlined style={{fontSize: 10}}/>
                                            </LabelButton>
                                        ) : (
                                            <Button
                                                size="small"
                                                icon={<TagOutlined/>}
                                                loading={labelMutation.isPending}
                                            >
                                                Label setzen <DownOutlined style={{fontSize: 10}}/>
                                            </Button>
                                        )}
                                    </Dropdown>
                                    {currentLabel !== "rejected" && (
                                        <Button
                                            size="small"
                                            danger
                                            icon={<CloseCircleOutlined/>}
                                            onClick={openDeclineModal}
                                        >
                                            Absagen
                                        </Button>
                                    )}
                                    <Tooltip title={isHidden ? "Wiederherstellen" : "Konversation löschen"}>
                                        <Button
                                            size="small"
                                            danger={!isHidden}
                                            icon={isHidden ? <UndoOutlined/> : <DeleteOutlined/>}
                                            loading={hideMutation.isPending}
                                            onClick={() =>
                                                msgDetails &&
                                                hideMutation.mutate({id: msgDetails.id, hidden: !isHidden})
                                            }
                                        />
                                    </Tooltip>
                                </Space>
                            </ChatHeader>

                            <MessagesArea>
                                {threadLoading && <CenterLoader><Spin/></CenterLoader>}
                                {messageList.map((msg, idx) => {
                                    const outgoing =
                                        ("sending_users_id" in msg && msg.sending_users_id === user?.id) ||
                                        msg.type === "outreach";
                                    return (
                                        <BubbleRow key={("id" in msg ? msg.id : idx)} $outgoing={outgoing}>
                                            <Bubble $outgoing={outgoing}>
                                                <BubbleText $outgoing={outgoing}>{msg.message}</BubbleText>
                                                <BubbleTime $outgoing={outgoing}>
                                                    {formatDistanceToNow(new Date(msg.created_at), {
                                                        addSuffix: true,
                                                        locale: de,
                                                    })}
                                                </BubbleTime>
                                            </Bubble>
                                        </BubbleRow>
                                    );
                                })}
                                <div ref={threadEndRef}/>
                            </MessagesArea>

                            <Composer>
                                <TextArea
                                    placeholder="Nachricht schreiben…"
                                    value={reply}
                                    onChange={(e) => setReply(e.target.value)}
                                    autoSize={{minRows: 1, maxRows: 5}}
                                    style={{flex: 1, borderRadius: 20, resize: "none"}}
                                />
                                <Button
                                    type="primary"
                                    icon={<SendOutlined/>}
                                    shape="circle"
                                    size="large"
                                    onClick={handleSend}
                                    disabled={!reply.trim() || sendMessageMutation.isPending}
                                    loading={sendMessageMutation.isPending}
                                />
                            </Composer>
                        </>
                    )}
                </Center>
            )}

            {/* RIGHT: Info Panel */}
            {!isMobile && msgDetails && (
                <InfoPanel>
                    <Title level={5} style={{marginBottom: 16}}>Kandidatendetails</Title>
                    <InfoBlock>
                        <Text strong style={{fontSize: 14, display: "block", marginBottom: 8}}>
                            {candidateName}
                        </Text>
                        <Space direction="vertical" size={4} style={{width: "100%"}}>
                            {contactVisible && candidate?.contact?.email && (
                                <Text type="secondary" style={{fontSize: 13}}>
                                    <MailOutlined style={{marginRight: 6}}/>
                                    {candidate.contact.email}
                                </Text>
                            )}
                            {contactVisible && candidate?.contact?.phone && (
                                <Text type="secondary" style={{fontSize: 13}}>
                                    <PhoneOutlined style={{marginRight: 6}}/>
                                    {candidate.contact.phone}
                                </Text>
                            )}
                            {candidate?.display?.city && (
                                <Text type="secondary" style={{fontSize: 13}}>
                                    <EnvironmentOutlined style={{marginRight: 6}}/>
                                    {candidate.location?.postal_code} {candidate.display.city}
                                </Text>
                            )}
                            {candidate?.main_job?.job_position && (
                                <Text type="secondary" style={{fontSize: 13}}>
                                    <BankOutlined style={{marginRight: 6}}/>
                                    {candidate.main_job.job_position}
                                    {candidate.main_job.job_experience ? `, ${candidate.main_job.job_experience}` : ""}
                                </Text>
                            )}
                        </Space>
                    </InfoBlock>

                    {job?.title && (
                        <InfoBlock>
                            <Text strong style={{fontSize: 14, display: "block", marginBottom: 8}}>
                                Stellendetails
                            </Text>
                            <Space direction="vertical" size={4} style={{width: "100%"}}>
                                <Text type="secondary" style={{fontSize: 13}}>
                                    <BankOutlined style={{marginRight: 6}}/>
                                    {job.title}
                                </Text>
                                {job.search_location_label && (
                                    <Text type="secondary" style={{fontSize: 13}}>
                                        <EnvironmentOutlined style={{marginRight: 6}}/>
                                        {job.search_location_label}
                                    </Text>
                                )}
                                {job.job_type_label && (
                                    <Text type="secondary" style={{fontSize: 13}}>
                                        <ClockCircleOutlined style={{marginRight: 6}}/>
                                        {job.job_type_label}
                                    </Text>
                                )}
                                {job.work_load_label && (
                                    <Text type="secondary" style={{fontSize: 13}}>
                                        <ClockCircleOutlined style={{marginRight: 6}}/>
                                        {job.work_load_label}
                                    </Text>
                                )}
                            </Space>
                        </InfoBlock>
                    )}

                    <InfoBlock>
                        <Text type="secondary" style={{fontSize: 12, display: "block", marginBottom: 4}}>
                            Status
                        </Text>
                        {labelCfg ? (
                            <Chip $bg={labelCfg.bg} $color={labelCfg.color}>{labelCfg.label}</Chip>
                        ) : (
                            <Text type="secondary" style={{fontSize: 13}}>Kein Label</Text>
                        )}
                    </InfoBlock>

                    {msgDetails.created_at && (
                        <InfoBlock>
                            <Text type="secondary" style={{fontSize: 12, display: "block", marginBottom: 4}}>
                                Kontaktaufnahme
                            </Text>
                            <Text style={{fontSize: 13}}>
                                {new Date(msgDetails.created_at).toLocaleDateString("de-DE", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                })}
                            </Text>
                        </InfoBlock>
                    )}

                    {msgDetails.key && (
                        <Button
                            icon={<UserOutlined/>}
                            block
                            type="primary"
                            style={{marginTop: 8}}
                            onClick={() => window.open(`/details/${msgDetails.key}`, "_blank", "noopener,noreferrer")}
                        >
                            Zum Kandidatenprofil
                        </Button>
                    )}
                </InfoPanel>
            )}

            <Modal
                open={declineOpen}
                title={`Absage an ${candidateName}`}
                onCancel={() => setDeclineOpen(false)}
                width={560}
                maskClosable={!declineMutation.isPending}
                footer={[
                    <Button
                        key="cancel"
                        onClick={() => setDeclineOpen(false)}
                        disabled={declineMutation.isPending}
                    >
                        Abbrechen
                    </Button>,
                    <Button
                        key="send"
                        type="primary"
                        danger
                        loading={declineMutation.isPending}
                        disabled={!declineText.trim()}
                        onClick={handleSendDecline}
                    >
                        Absage senden
                    </Button>,
                ]}
            >
                <Text type="secondary" style={{display: "block", marginBottom: 12}}>
                    {`Die Nachricht wird gesendet und das Label „Abgelehnt" wird gesetzt.`}
                </Text>
                <TextArea
                    rows={9}
                    value={declineText}
                    onChange={(e) => setDeclineText(e.target.value)}
                />
            </Modal>
        </Outer>
    );
};

// ── Styled ───────────────────────────────────────────────────────────────
const Outer = styled.div`
    display: flex;
    height: calc(100vh - 80px);
    background: #fff;
    border-radius: 10px;
    overflow: hidden;

    @media (max-width: 767px) {
        height: calc(100vh - 60px);
        border-radius: 0;
    }
`;

const Sidebar = styled.div`
    width: ${SIDEBAR_WIDTH}px;
    border-right: 1px solid #f0f0f0;
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    background: #fff;

    @media (max-width: 767px) {
        width: 100%;
    }
`;

const SidebarHeader = styled.div`
    padding: 16px 16px 0;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
`;

const FilterRow = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 10px;
`;

const TabsRow = styled.div`
    display: flex;
    gap: 4px;
    flex-wrap: wrap;
    padding-bottom: 12px;
`;

const MassActionBar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    margin-bottom: 12px;
    background: #f0f5ff;
    border: 1px solid #d6e4ff;
    border-radius: 6px;
`;

const MassCount = styled.span`
    font-size: 12px;
    color: #595959;
    margin-right: auto;
`;

const SelectAllLabel = styled.label`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #595959;
    cursor: pointer;
    user-select: none;
`;

const TabButton = styled.button<{$active: boolean}>`
    padding: 4px 12px;
    border-radius: 16px;
    border: none;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    background: ${(p) => (p.$active ? colors.main : "#f5f5f5")};
    color: ${(p) => (p.$active ? "#fff" : "#595959")};
    transition: background 0.15s ease;
`;

const TabCount = styled.span`
    margin-left: 4px;
    opacity: 0.7;
`;

const ListArea = styled.div`
    flex: 1;
    overflow-y: auto;

    &::-webkit-scrollbar { display: none; }
`;

const EmptyList = styled.div`
    text-align: center;
    padding: 40px 16px;
`;

const RowSpinner = styled.div`
    display: flex;
    justify-content: center;
    padding: 16px 0;
`;

const Center = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: #fff;
`;

const EmptyState = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #bfbfbf;
    text-align: center;
    padding: 24px;
`;

const CenterLoader = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
`;

const ChatHeader = styled.div`
    padding: 12px 20px;
    border-bottom: 1px solid #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
`;

const ChatHeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
`;

const ChatHeaderTitles = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const MessagesArea = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: #f5f5f5;
`;

const BubbleRow = styled.div<{$outgoing: boolean}>`
    display: flex;
    justify-content: ${(p) => (p.$outgoing ? "flex-end" : "flex-start")};
    margin-bottom: 16px;
`;

const Bubble = styled.div<{$outgoing: boolean}>`
    max-width: 70%;
    padding: 12px 16px;
    border-radius: ${(p) => (p.$outgoing ? "16px 16px 4px 16px" : "16px 16px 16px 4px")};
    background: ${(p) => (p.$outgoing ? colors.main : "#fff")};
    color: ${(p) => (p.$outgoing ? "#fff" : "#262626")};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

    @media (max-width: 767px) {
        max-width: 85%;
    }
`;

const BubbleText = styled.p<{$outgoing: boolean}>`
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: ${(p) => (p.$outgoing ? "#fff" : "#262626")};
    white-space: pre-wrap;
    word-break: break-word;
`;

const BubbleTime = styled.div<{$outgoing: boolean}>`
    text-align: right;
    margin-top: 6px;
    font-size: 11px;
    color: ${(p) => (p.$outgoing ? "rgba(255,255,255,0.8)" : "#8c8c8c")};
`;

const Composer = styled.div`
    padding: 12px 20px;
    border-top: 1px solid #f0f0f0;
    background: #fff;
    display: flex;
    gap: 12px;
    align-items: flex-end;
`;

const InfoPanel = styled.div`
    width: ${INFO_PANEL_WIDTH}px;
    border-left: 1px solid #f0f0f0;
    background: #fafafa;
    padding: 20px 16px;
    overflow-y: auto;
    flex-shrink: 0;
`;

const InfoBlock = styled.div`
    margin-bottom: 20px;
`;

const MenuChip = styled.span<{$bg: string; $color: string}>`
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    line-height: 22px;
    padding: 0 10px;
    border-radius: 4px;
    background: ${(p) => p.$bg};
    color: ${(p) => p.$color};
    border: 1px solid ${(p) => p.$color}33;
    white-space: nowrap;
`;

const MenuClear = styled.span`
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    color: #8c8c8c;
    line-height: 22px;
`;

const LabelButton = styled.button<{$bg: string; $color: string}>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    line-height: 22px;
    padding: 0 10px;
    border-radius: 4px;
    background: ${(p) => p.$bg};
    color: ${(p) => p.$color};
    border: 1px solid ${(p) => p.$color}33;
    cursor: pointer;
    white-space: nowrap;
    transition: filter 0.15s ease;

    &:hover:not(:disabled) { filter: brightness(0.96); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Chip = styled.span<{$bg: string; $color: string}>`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
    line-height: 20px;
    padding: 0 10px;
    border-radius: 4px;
    background: ${(p) => p.$bg};
    color: ${(p) => p.$color};
    border: 1px solid ${(p) => p.$color}20;
    white-space: nowrap;
`;
